import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { blockWorkspace } from "../../api/workspaceSettings";
import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";
import { BlockWorkspaceFormData, createBlockWorkspaceSchema } from "../../schemas";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

interface BlockWorkspaceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BlockWorkspaceDialog = ({ isOpen, onClose }: BlockWorkspaceDialogProps) => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { refetch } = useWorkspaceSettingsStore();
  const { refetch: refetchWorkspaceInfo } = useWorkspaceInformationStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonLength, setReasonLength] = useState(0);
  const [hasFutureLessonsError, setHasFutureLessonsError] = useState(false);
  const blockWorkspaceSchema = createBlockWorkspaceSchema(t);

  const form = useForm<BlockWorkspaceFormData>({
    resolver: zodResolver(blockWorkspaceSchema),
    defaultValues: {
      reason: "",
    },
  });

  const getErrorMessage = (errorCode: string, errorMessage?: string) => {
    const errorMap: Record<string, string> = {
      future_lessons_exist: t(
        "settings.blockWorkspace.errors.futureLessonsExist",
        "You cannot block this workspace because you have reserved future lessons. Please cancel all your scheduled lessons first.",
      ),
      not_workspace_member: t(
        "settings.blockWorkspace.errors.notWorkspaceMember",
        "You are not a member of this workspace.",
      ),
      user_already_blocked_workspace: t(
        "settings.blockWorkspace.errors.alreadyBlocked",
        "You have already blocked this workspace.",
      ),
      user_blocked_by_others_cannot_act: t(
        "settings.blockWorkspace.errors.blockedByOthers",
        "You cannot block this workspace because you have been blocked by someone else.",
      ),
      not_authenticated: t(
        "settings.blockWorkspace.errors.notAuthenticated",
        "Authentication required",
      ),
      insufficient_permissions: t(
        "settings.blockWorkspace.errors.insufficientPermissions",
        "Insufficient permissions",
      ),
    };

    return (
      errorMap[errorCode] ||
      errorMessage ||
      t(
        "workspaces:settings.blockWorkspace.errors.generic",
        "An error occurred while processing your request",
      )
    );
  };

  const handleSubmit = async (data: BlockWorkspaceFormData) => {
    if (!workspaceId) return;

    setIsSubmitting(true);
    try {
      // Only include reason in request if it's not empty
      const requestData = data.reason ? { reason: data.reason } : null;

      await blockWorkspace(workspaceId, requestData);

      // Refetch settings to get updated blocking status
      await refetch(workspaceId);

      form.reset();
      setReasonLength(0);
      onClose();

      // Refetch workspace basics data
      await refetchWorkspaceInfo(workspaceId);
    } catch (error: any) {
      console.error("Failed to block workspace:", error);

      const errorCode = error?.response?.data?.code;
      const errorMessage = error?.response?.data?.message;
      const errorMsg = getErrorMessage(errorCode, errorMessage);

      // Check if it's a future lessons error
      if (errorCode === "future_lessons_exist") {
        setHasFutureLessonsError(true);
      }

      // Handle validation errors specifically
      if (error?.response?.status === 422 && error?.response?.data?.errors) {
        const validationErrors = error.response.data.errors;

        if (validationErrors.reason) {
          form.setError("reason", {
            type: "server",
            message: validationErrors.reason[0],
          });
        }
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setReasonLength(0);
      setHasFutureLessonsError(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-background/95 border-border/50 h-fit max-h-dvh max-w-2xl p-0 shadow-2xl backdrop-blur-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden rounded-xl opacity-10">
          <div className="absolute -top-20 -right-24 h-48 w-48 rounded-full bg-gradient-to-br from-red-500 to-orange-500" />
          <div className="absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-gradient-to-tr from-red-600 to-pink-500" />
        </div>

        <ScrollArea className="h-full max-h-dvh">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="relative p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-6">
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto mb-4 flex size-16 items-center justify-center"
                initial={{ scale: 0 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
              >
                <div className="flex size-full items-center justify-center rounded-full border-2 border-red-500/30 bg-red-500/20 backdrop-blur-sm">
                  <Icon
                    className="text-red-500"
                    height="1.5rem"
                    icon="mdi:block-helper"
                    width="1.5rem"
                  />
                </div>
              </motion.div>
              <DialogTitle className="text-accent-foreground text-center text-2xl font-bold">
                {t("workspaces:settings.blockWorkspace.dialog.title", "Block Workspace")}
              </DialogTitle>
              <div className="text-muted-foreground text-center text-sm">
                {t("workspaces:settings.blockWorkspace.dialog.description")}
              </div>
            </DialogHeader>

            <div className="space-y-6">
              <Form {...form}>
                <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
                  <FormField
                    control={form.control}
                    name="reason"
                    render={({ field }) => (
                      <div>
                        <FormItem className="space-y-3">
                          <div className="flex items-center justify-between">
                            <FormLabel className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                              <Icon height={"1.25rem"} icon="mdi:text-box" width={"1.25rem"} />
                              {t("workspaces:settings.blockWorkspace.dialog.reasonLabel")}
                              <span className="text-muted-foreground ml-1 text-sm font-normal">
                                ({t("common:optional")})
                              </span>
                            </FormLabel>
                            <span className="text-muted-foreground text-sm">
                              {reasonLength}/{500}
                            </span>
                          </div>
                          <FormControl>
                            <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                              <CardContent className="p-0">
                                <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                                  <Textarea
                                    className="[field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                    placeholder={t(
                                      "settings.blockWorkspace.dialog.reasonPlaceholder",
                                    )}
                                    {...field}
                                    maxLength={500}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      setReasonLength(e.target.value.length);
                                    }}
                                  />
                                </ScrollArea>
                              </CardContent>
                            </Card>
                          </FormControl>
                        </FormItem>
                      </div>
                    )}
                  />

                  {/* Action buttons */}
                  <div className="flex justify-center gap-4 pt-6">
                    <Button
                      className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground min-w-32 rounded-xl"
                      disabled={isSubmitting}
                      type="button"
                      variant="ghost"
                      onClick={handleClose}
                    >
                      {t("common:cancel")}
                    </Button>
                    <Button
                      className="min-w-32 gap-2 rounded-xl bg-red-500 text-white hover:bg-red-600"
                      disabled={isSubmitting || hasFutureLessonsError}
                      type="submit"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon className="animate-spin" icon="lucide:loader-2" />
                          {t("workspaces:settings.blockWorkspace.dialog.blocking")}
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:block-helper" />
                          {t("workspaces:settings.blockWorkspace.dialog.blockButton")}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
